import React, { useState, useEffect, useCallback } from 'react';
import { getAgenturLeadStats, getUsersByAgentur } from '../../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SubTeamDashboard from './SubTeamDashboard';
import DashboardNav from './DashboardNav';
import { useNavigate } from 'react-router-dom';
import DateRangeFilter from '../common/DateRangeFilter';

function AgenturDashboard({ user, selectedAgentur, onBackToOrganization }) {
  const navigate = useNavigate();
  
  // Date Range State - Standard: Letzte 14 Tage
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      days: 14
    };
  });

  const [teamStats, setTeamStats] = useState({
    totalLeads: 0,
    qualified: 0,
    noInterest: 0,
    revoked: 0,
    notQualified: 0,
    open: 0,
    kwhTooLow: 0,
    monumentProtection: 0,
    roofNeedsRenovation: 0,
    technicallyNotPossible: 0,
    duplicate: 0,
    notWithinOneYear: 0,
    loading: true,
    error: null
  });

  const [chartData, setChartData] = useState({
    allPerformers: [],
    topPerformers: [],
    dailyLeads: [],
    loading: true
  });

  const [performerIndex, setPerformerIndex] = useState(0);
  const performersPerPage = 3;

  // Reset pagination index when data changes
  useEffect(() => {
    setPerformerIndex(0);
  }, [chartData.allPerformers.length]);

  // Ensure performerIndex doesn't exceed available pages
  useEffect(() => {
    const totalPages = getTotalPages();
    if (performerIndex >= totalPages) {
      setPerformerIndex(Math.max(0, totalPages - 1));
    }
  }, [performerIndex, chartData.allPerformers.length]);

  // State für Agentur-Mitglieder
  const [agenturMembers, setAgenturMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [userSubTeamInfo, setUserSubTeamInfo] = useState(null);
  const [subTeams, setSubTeams] = useState([]);
  const [subTeamsLoading, setSubTeamsLoading] = useState(false);
  const [selectedSubTeam, setSelectedSubTeam] = useState(null);

  // Bestimme ob User Teamleiter ist oder ob eine Agentur ausgewählt wurde (für Org-Leader)
  const isTeamLeader = user?.isTeamlead || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isAdmin = user?.isAdmin || false;
  const isAgenturLeader = user?.isAgenturLeader || false;
  
  // Verwende die ausgewählte Agentur
  const currentAgentur = selectedAgentur;
  const canViewAgenturData = isTeamLeader || isOrgLeader || isAdmin || isAgenturLeader;

  console.log("User : ", user)

  // Handler für Navigation
  const handleLogout = () => {
    navigate('/');
  };
  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'agentur-dashboard') {
      navigate(`/agentur-dashboard/${selectedAgentur?.id}`);
    } else if (section === 'mitglieder') {
      navigate(`/agenturen/${selectedAgentur?.id}/mitglieder`);
    } else if (section === 'leads') {
      navigate(`/agenturen/${selectedAgentur?.id}/leads`);
    }
  };

  // Pagination Funktionen für Mitarbeiter Performance
  const getCurrentPagePerformers = () => {
    const startIndex = performerIndex * performersPerPage;
    const endIndex = startIndex + performersPerPage;
    return chartData.allPerformers.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const totalPages = Math.ceil(chartData.allPerformers.length / performersPerPage);
    return Math.max(1, totalPages); // Mindestens 1 Seite
  };

  const handlePreviousPage = () => {
    setPerformerIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    setPerformerIndex(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Agentur-Mitglieder laden
  useEffect(() => {
    const loadAgenturMembers = async () => {
      if (!canViewAgenturData || !currentAgentur?.id) return;
      
      setMembersLoading(true);
      try {
        const response = await getUsersByAgentur(user.id, currentAgentur.id);
        
        if (response.success) {
          setAgenturMembers(response.users || []);
        } else {
          console.error("Fehler beim Laden der Agentur-Mitglieder:", response.error);
          setAgenturMembers([]);
        }
      } catch (err) {
        console.error("Fehler beim Laden der Agentur-Mitglieder:", err);
        setAgenturMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };

    loadAgenturMembers();
  }, [canViewAgenturData, user?.id, currentAgentur?.id]);

  // Lead-Statistiken laden
  useEffect(() => {
    // Reset performer index when data changes
    setPerformerIndex(0);
    
    const loadAgenturStatistics = async () => {
      if (!canViewAgenturData || !currentAgentur?.id) {
        setTeamStats(prev => ({ ...prev, loading: false }));
        setChartData(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        setTeamStats(prev => ({ ...prev, loading: true, error: null }));
        setChartData(prev => ({ ...prev, loading: true }));
        
        const response = await getAgenturLeadStats(user.id, currentAgentur.id, null, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });
        
        if (response.success) {
          const s = response.stats || {};
          setTeamStats({
            totalLeads: typeof s.totalLeads === 'number' ? s.totalLeads : 0,
            qualified: s.qualified ?? 0,
            noInterest: s.noInterest ?? 0,
            revoked: s.revoked ?? 0,
            notQualified: s.notQualified ?? 0,
            open: s.open ?? 0,
            kwhTooLow: s.kwhTooLow ?? 0,
            monumentProtection: s.monumentProtection ?? 0,
            roofNeedsRenovation: s.roofNeedsRenovation ?? 0,
            technicallyNotPossible: s.technicallyNotPossible ?? 0,
            duplicate: s.duplicate ?? 0,
            notWithinOneYear: s.notWithinOneYear ?? 0,
            loading: false,
            error: null,
          });
          processChartData(response);
        } else {
          setTeamStats(prev => ({
            ...prev,
            loading: false,
            error: response.error || 'Fehler beim Laden der Agentur-Statistiken'
          }));
          if (agenturMembers && agenturMembers.length > 0) {
            processChartData({ daily: [], byUser: [] });
          } else {
            setChartData(prev => ({ ...prev, loading: false }));
          }
        }
      } catch (err) {
        setTeamStats(prev => ({
          ...prev,
          loading: false,
          error: 'Netzwerkfehler: ' + err.message
        }));
        if (agenturMembers && agenturMembers.length > 0) {
          processChartData({ daily: [], byUser: [] });
        } else {
          setChartData(prev => ({ ...prev, loading: false }));
        }
      }
    };

    loadAgenturStatistics();
  }, [canViewAgenturData, user?.id, currentAgentur?.id, agenturMembers, dateRange]);

  // Chart-Daten aktualisieren, wenn sich der performerIndex ändert
  useEffect(() => {
    if (chartData.allPerformers.length > 0) {
      const topPerformers = getCurrentPagePerformers();
      setChartData(prev => ({
        ...prev,
        topPerformers
      }));
    }
  }, [performerIndex, chartData.allPerformers]);

  // Custom Tooltip für Top Performer Chart
  const TopPerformerTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      console.log("Data : ", data)
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          fontSize: '14px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#2d3748' }}>
            {data.fullName}
          </p>
          <p style={{ margin: '0 0 4px 0', color: '#4a6de5' }}>
            ■ Gesamt: {data.Gesamt}
          </p>
          <p style={{ margin: '0 0 4px 0', color: '#1a365d' }}>
            ■ Qualifiziert: {data.Qualifiziert}
          </p>
          <p style={{ margin: '0 0 4px 0', color: '#ed8936' }}>
            ■ Kein Interesse: {data["Kein Interesse"]}
          </p>
          <p style={{ margin: '0 0 4px 0', color: '#d69e2e' }}>
            ■ KWh zu niedrig: {data["KWh zu niedrig"]}
          </p>
          <p style={{ margin: '0 0 4px 0', color: '#e53e3e' }}>
            ■ Technisch nicht möglich: {data["Technisch nicht möglich"]}
          </p>
          <p style={{ margin: '0', color: '#e53e3e' }}>
            ■ Widerrufen: {data["Widerrufen"]}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip für Daily Leads Chart
  const DailyLeadsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          fontSize: '14px'
        }}>
          <p style={{ margin: '0', color: '#2d3748' }}>
            {label}: {payload[0].value} Leads
          </p>
        </div>
      );
    }
    return null;
  };

  // Prozentsatz berechnen
  const getPercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // Navigation für Mitarbeiter Performance (verwendet die korrekten Paginierungsfunktionen)
  const canGoPrevious = performerIndex > 0;
  const canGoNext = performerIndex < getTotalPages() - 1;

  // Chart-Daten aus server-seitig aggregierten Kennzahlen (keine Roh-Lead-Liste nötig)
  const processChartData = (statsResponse) => {
    const byUser = statsResponse?.byUser || [];
    const daily = statsResponse?.daily || [];

    if (!agenturMembers || agenturMembers.length === 0) {
      setChartData((prev) => ({
        ...prev,
        dailyLeads: daily,
        allPerformers: [],
        topPerformers: [],
        loading: true,
      }));
      return;
    }

    const umap = new Map(byUser.map((u) => [u.userId, u]));
    const allPerformers = agenturMembers
      .map((member) => {
        const memberName = member.full_name || member.email || 'Unbekannt';
        const leadData = umap.get(member.id) || {
          total: 0,
          qualified: 0,
          noInterest: 0,
          kwhTooLow: 0,
          revoked: 0,
          technicallyNotPossible: 0,
        };
        return {
          name: memberName.length > 12 ? memberName.substring(0, 12) + '...' : memberName,
          fullName: memberName,
          Gesamt: leadData.total,
          Qualifiziert: leadData.qualified,
          'Kein Interesse': leadData.noInterest,
          'KWh zu niedrig': leadData.kwhTooLow,
          'Technisch nicht möglich': leadData.technicallyNotPossible,
          Widerrufen: leadData.revoked,
          memberId: member.id,
          role: member.role_name || 'Benutzer',
        };
      })
      .sort((a, b) => b.Gesamt - a.Gesamt);

    const topPerformers = allPerformers.slice(
      performerIndex * performersPerPage,
      (performerIndex + 1) * performersPerPage
    );
    const dailyLeads = daily.map((d) => ({ date: d.date, leads: d.leads }));

    setChartData({
      allPerformers,
      topPerformers,
      dailyLeads,
      loading: false,
    });
  };

  // Wenn ein Sub-Team ausgewählt ist, zeige Sub-Team-Dashboard
  if (selectedSubTeam) {
    return (
      <SubTeamDashboard 
        user={user}
        selectedSubTeam={selectedSubTeam}
        onBackToTeam={() => setSelectedSubTeam(null)}
      />
    );
  }

  // Wenn keine Berechtigung, zeige Meldung
  if (!canViewAgenturData) {
    return (
      <div className="team-dashboard-container">
        <div className="team-dashboard-header">
          <h2>Agentur Dashboard</h2>
        </div>
        <div className="no-team-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <p>Agentur Dashboard ist nur für Teamleiter oder Org-Leader verfügbar.</p>
          <span>Sie haben keine Berechtigung, Agentur-Statistiken einzusehen.</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardNav
        activeSection="agentur-dashboard"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => navigate(`/agenturen/${selectedAgentur?.id}/mitglieder`)}
        onOpenMembersManagement={() => navigate(`/agenturen/${selectedAgentur?.id}/mitglieder`)}
        onViewOrganizationLeads={() => navigate(`/agenturen/${selectedAgentur?.id}/leads`)}
        onViewTeamLeads={() => {}}
        showOrgManagement={true}
        showMembersManagement={true}
        showOrganizationLeads={true}
      />
      <div className="team-dashboard-container">
        <div className="team-dashboard-header">
          {/* Back Button für Org-Leader */}
          {selectedAgentur && onBackToOrganization && (
            <button 
              onClick={onBackToOrganization}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#374151',
                marginBottom: '16px',
                transition: 'all 0.2s ease',
                fontWeight: '500',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.borderColor = '#9ca3af';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5"/>
                <path d="M12 19l-7-7 7-7"/>
              </svg>
              Zurück zur Organisation
            </button>
          )}
          
          <h2>
            {userSubTeamInfo?.sub_team_name ? 
              `Sub-Team Dashboard - ${currentAgentur?.name || 'Meine Agentur'} (${userSubTeamInfo.sub_team_name})` : 
              `Agentur Dashboard - ${currentAgentur?.name || 'Meine Agentur'}`
            }
          </h2>
          <span className="team-name">
            {userSubTeamInfo?.sub_team_name ? 
              `${currentAgentur?.name || 'Meine Agentur'} (${userSubTeamInfo.sub_team_name})` : 
              currentAgentur?.name || 'Meine Agentur'
            }
          </span>
        </div>
        
        {/* Date Range Filter */}
        <DateRangeFilter 
          onDateRangeChange={setDateRange}
          initialDays={14}
        />
        
        {teamStats.loading ? (
          <div className="stats-loading">
            <div className="loading-spinner"></div>
            <span>Lade Agentur-Statistiken...</span>
          </div>
        ) : teamStats.error ? (
          <div className="stats-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>{teamStats.error}</span>
          </div>
        ) : (
          <>
            {/* Sub-Teams Sektion - für Teamleiter */}
            {isTeamLeader && subTeams.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '1.125rem', 
                  fontWeight: '600', 
                  color: '#2d3748',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  Sub-Teams verwalten
                </h3>
                
                {subTeamsLoading ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px', 
                    color: '#718096',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div className="loading-spinner"></div>
                    <span>Lade Sub-Teams...</span>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '16px'
                  }}>
                    {subTeams.map(subTeam => (
                      <div
                        key={subTeam.id}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: selectedSubTeam?.id === subTeam.id ? '#f7fafc' : '#ffffff'
                        }}
                        onClick={() => setSelectedSubTeam(selectedSubTeam?.id === subTeam.id ? null : subTeam)}
                        onMouseOver={(e) => {
                          e.target.style.borderColor = '#4a6de5';
                          e.target.style.boxShadow = '0 2px 8px rgba(74, 109, 229, 0.15)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <h4 style={{
                            margin: '0',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#2d3748'
                          }}>
                            {subTeam.name}
                          </h4>
                          <span style={{
                            backgroundColor: '#4a6de5',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            {subTeam.member_count || 0} Mitglieder
                          </span>
                        </div>
                        
                        {subTeam.description && (
                          <p style={{
                            margin: '0 0 12px 0',
                            fontSize: '0.875rem',
                            color: '#718096',
                            lineHeight: '1.4'
                          }}>
                            {subTeam.description}
                          </p>
                        )}
                        
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '0.75rem',
                          color: '#a0aec0'
                        }}>
                          <span>Team: {subTeam.team_name}</span>
                          <span>ID: {subTeam.id}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="team-stats-grid">
              {/* Gesamte Leads */}
              <div className="stat-card total-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.totalLeads}</div>
                  <div className="stat-label">Leads eingegangen</div>
                </div>
              </div>
              
              {/* Offene Leads */}
              <div className="stat-card open-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.open}</div>
                  <div className="stat-label">Offen</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.open, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>

              {/* Qualifizierte Leads */}
              <div className="stat-card qualified-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.qualified}</div>
                  <div className="stat-label">Qualifiziert</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.qualified, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Kein Interesse */}
              <div className="stat-card no-interest-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.noInterest}</div>
                  <div className="stat-label">Kein Interesse</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.noInterest, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Widerrufen */}
              <div className="stat-card revoked-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18l-2 13H5L3 6z"></path>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.revoked}</div>
                  <div className="stat-label">Widerrufen</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.revoked, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Nicht qualifiziert */}
              <div className="stat-card not-qualified-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.notQualified}</div>
                  <div className="stat-label">Nicht qualifiziert</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.notQualified, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* KWh zu niedrig */}
              <div className="stat-card kwh-too-low-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.kwhTooLow}</div>
                  <div className="stat-label">KWh zu niedrig</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.kwhTooLow, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Denkmalschutz */}
              <div className="stat-card monument-protection-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.monumentProtection}</div>
                  <div className="stat-label">Denkmalschutz</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.monumentProtection, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Dach muss saniert werden */}
              <div className="stat-card roof-renovation-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9,22 9,12 15,12 15,22"></polyline>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.roofNeedsRenovation}</div>
                  <div className="stat-label">Dach saniert werden</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.roofNeedsRenovation, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Technisch nicht möglich */}
              <div className="stat-card technically-not-possible-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.technicallyNotPossible}</div>
                  <div className="stat-label">Technisch nicht möglich</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.technicallyNotPossible, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>
              
              {/* Dublette */}
              <div className="stat-card duplicate-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{teamStats.duplicate}</div>
                  <div className="stat-label">Dublette</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.duplicate, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>

              {/* Nicht innerhalb von 1 Jahr */}
              <div className="stat-card not-within-one-year-leads">
                <div className="stat-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12,6 12,12 16,14"></polyline>
                  </svg>
                </div>
                                  <div className="stat-content">
                    <div className="stat-number">{teamStats.notWithinOneYear}</div>
                  <div className="stat-label">Nicht innerhalb von 1 Jahr</div>
                  <div className="stat-percentage">
                    {getPercentage(teamStats.notWithinOneYear, teamStats.totalLeads)}%
                  </div>
                </div>
              </div>

            </div>

            {/* Chart-Sektion */}
            <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr',
            gap: '24px', 
            marginTop: '32px'
            }}>
              {/* Top Performer Chart */}
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ 
                    margin: '0', 
                    fontSize: '1.125rem', 
                    fontWeight: '600', 
                    color: '#2d3748' 
                  }}>
                    Mitarbeiter Performance
                  </h3>
                  {chartData.allPerformers.length > performersPerPage && (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      <button
                        onClick={handlePreviousPage}
                        disabled={!canGoPrevious}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          background: canGoPrevious ? 'white' : '#f7fafc',
                          color: canGoPrevious ? '#4a5568' : '#a0aec0',
                          cursor: canGoPrevious ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (canGoPrevious) {
                            e.target.style.background = '#f7fafc';
                            e.target.style.borderColor = '#cbd5e0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canGoPrevious) {
                            e.target.style.background = 'white';
                            e.target.style.borderColor = '#e2e8f0';
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15,18 9,12 15,6"></polyline>
                        </svg>
                        Zurück
                      </button>
                      <span style={{
                        fontSize: '14px',
                        color: '#718096',
                        minWidth: '60px',
                        textAlign: 'center'
                      }}>
                        {performerIndex + 1} / {getTotalPages()}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={!canGoNext}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          background: canGoNext ? 'white' : '#f7fafc',
                          color: canGoNext ? '#4a5568' : '#a0aec0',
                          cursor: canGoNext ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (canGoNext) {
                            e.target.style.background = '#f7fafc';
                            e.target.style.borderColor = '#cbd5e0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canGoNext) {
                            e.target.style.background = 'white';
                            e.target.style.borderColor = '#e2e8f0';
                          }
                        }}
                      >
                        Weiter
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9,18 15,12 9,6"></polyline>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {chartData.loading ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px', 
                    color: '#718096',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div className="loading-spinner"></div>
                    <span>Lade Chart-Daten...</span>
                  </div>
                ) : chartData.topPerformers.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={chartData.topPerformers} 
                      margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#4a5568' }}
                        interval={0}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#4a5568' }}
                      />
                      <Tooltip content={<TopPerformerTooltip />} />
                      <Bar 
                        dataKey="Gesamt" 
                        fill="#4a6de5" 
                        radius={[4, 4, 0, 0]}
                        name="Gesamt"
                      />
                      <Bar 
                        dataKey="Qualifiziert" 
                        fill="#1a365d" 
                        radius={[4, 4, 0, 0]}
                        name="Qualifiziert"
                      />
                      <Bar 
                        dataKey="Kein Interesse" 
                        fill="#ed8936" 
                        radius={[4, 4, 0, 0]}
                        name="Kein Interesse"
                      />
                      <Bar 
                        dataKey="KWh zu niedrig" 
                        fill="#d69e2e" 
                        radius={[4, 4, 0, 0]}
                        name="KWh zu niedrig"
                      />
                      <Bar 
                        dataKey="Technisch nicht möglich" 
                        fill="#e53e3e" 
                        radius={[4, 4, 0, 0]}
                        name="Technisch nicht möglich"
                      />
                      <Bar 
                        dataKey="Widerrufen" 
                        fill="#e53e3e" 
                        radius={[4, 4, 0, 0]}
                        name="Widerrufen"
                      />
                      
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px', 
                    color: '#a0aec0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <path d="M9 9h6v6H9z"></path>
                    </svg>
                    <span>Keine Agentur-Daten verfügbar</span>
                  </div>
                )}
              </div>

              {/* Daily Leads Chart */}
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '1.125rem', 
                  fontWeight: '600', 
                  color: '#2d3748' 
                }}>
                  Leads pro Tag
                </h3>
                {chartData.loading ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px', 
                    color: '#718096',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div className="loading-spinner"></div>
                    <span>Lade Chart-Daten...</span>
                  </div>
                ) : chartData.dailyLeads.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={chartData.dailyLeads} 
                      margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: '#4a5568' }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#4a5568' }}
                      />
                      <Tooltip content={<DailyLeadsTooltip />} />
                      <Bar 
                        dataKey="leads" 
                        fill="#4a6de5" 
                        radius={[2, 2, 0, 0]}
                        name="Leads"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px', 
                    color: '#a0aec0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <path d="M9 9h6v6H9z"></path>
                    </svg>
                    <span>Keine Daily-Daten verfügbar</span>
                  </div>
                )}
              </div>
            </div>

            {/* Zusätzliche Informationen */}
            {teamStats.totalLeads > 0 && (
              <div className="team-insights">
                <h3>Insights</h3>
                <div className="insights-grid">
                  <div className="insight-item">
                    <span className="insight-label">Erfolgsrate:</span>
                    <span className="insight-value qualified-text">
                      {getPercentage(teamStats.qualified, teamStats.totalLeads)}%
                    </span>
                  </div>
                  <div className="insight-item">
                    <span className="insight-label">Verlustrate:</span>
                    <span className="insight-value negative-text">
                      {getPercentage(teamStats.noInterest + teamStats.revoked + teamStats.notQualified + teamStats.kwhTooLow + teamStats.monumentProtection + teamStats.roofNeedsRenovation + teamStats.technicallyNotPossible + teamStats.duplicate, teamStats.totalLeads)}%
                    </span>
                  </div>
                  <div className="insight-item">
                    <span className="insight-label">Durchschnitt/Tag:</span>
                    <span className="insight-value neutral-text">
                      {Math.round(teamStats.totalLeads / 30)} Leads
                    </span>
                  </div>
                  <div className="insight-item">
                    <span className="insight-label">Top Performer:</span>
                    <span className="insight-value neutral-text">
                      {chartData.topPerformers.length > 0 ? chartData.topPerformers[0].fullName : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Kein Data Zustand */}
        {!teamStats.loading && !teamStats.error && teamStats.totalLeads === 0 && (
          <div className="no-team-data">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
              <line x1="9" y1="9" x2="9.01" y2="9"></line>
              <line x1="15" y1="9" x2="15.01" y2="9"></line>
            </svg>
            <p>Keine Leads in den letzten 2 Wochen gefunden.</p>
            <span>Beginnen Sie mit der Lead-Generierung für Ihre Agentur!</span>
          </div>
        )}
      </div>
    </>
  );
}

export default AgenturDashboard;